import React, { useState, useEffect, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import styles from "./CustomerReservation.module.scss";
import crStyles from "./crStyles.js";
import { Link } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import NavigateNext from "@mui/icons-material/NavigateNext";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import { withTranslation } from "react-i18next";
import HasPermissionTo from "../../common/HasPermissionTo";
import SortingIcon from "../../shared/sortingIcon/SortingIcon";
import TitleCase from "../../utils/TitleCase";
import CustomerReservationsClient from "../../shared/clients/CustomerReservationsClient";
import UiDateUtils from "../../utils/UiDateUtils";
import LowerCaseText from "../../utils/LowerCaseText";
import UpperCaseText from "../../utils/UpperCaseText";
import { PURCHASE_REQUEST_STATUS } from "../../common/Constants";
import ConvertButton from "../../imgs/convert.svg";
import { FORMAT_COMMON_ID, GET_DEALER_NAME } from "../../utils/EmpUtil";
import { displayError, ERROR_PROCESSING } from "../../errors/ErrorConstants";
import DeliveryScheduleDialog from "./DeliveryScheduleDialog";

const STATUS_MAP = {
  CREATED: "created",
  RESERVATION_FAILED: "created",
  ORDER_RESERVED: "created",
  CONTRACT_REQUESTED: "confirmed",
  CONTRACT_REQUEST_FAILED: "confirmed",
  CONTRACT_FAILED: "confirmed",
  CONTRACT_CANCEL_REQUESTED: "ordered",
  CONTRACT_CANCEL_REQUEST_FAILED: "ordered",
  RESERVATION_CANCELLED: "cancelled",
  DEALER_CANCELLED: "cancelled",
  CONTRACT_CANCELLED: "cancelled",
  CONVERTED_TO_OFFLINE: "amended",
  AMENDED: "amended",
  ORDER_CONTRACTED: "contracted",
  ORDER_CONFIRMED: "submitted",
  CONTRACT_CANCEL_FAILED: "ordered",
  RESERVATION_CANCELLATION_FAILED: "contracted",
  AMEND_CONTRACT_REQUEST_FAILED: "contracted",
  AMEND_CONTRACT_CANCEL_REQUEST_FAILED: "contracted",
  ORDERED: "ordered",
};

function CustomerReservationTable(props) {
  const [sortDir, setSortDir] = useState(undefined);
  const [sortKey, setSortKey] = useState(undefined);
  const [sortByString, setSortByString] = useState(undefined);
  const [iconUp, setIconUp] = useState(true);
  const [loading, setLoading] = useState(false);
  const [reservationId, setReservationId] = useState(undefined);
  const [hasError, setHasError] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (
      props.lastPath &&
      props.lastPath.includes("/customer-reservations/order-details/") &&
      sessionStorage.getItem(
        "sortKey_" + props.commonDealerId.split("|").join("%7C")
      ) &&
      sessionStorage.getItem(
        "sortKey_" + props.commonDealerId.split("|").join("%7C")
      ) !== "undefined"
    ) {
      const storedSortKey = sessionStorage.getItem(
        "sortKey_" + props.commonDealerId.split("|").join("%7C")
      );
      const sortDirection = sessionStorage.getItem(
        "sortDirection_" + props.commonDealerId.split("|").join("%7C")
      );
      const storedSortByString = sessionStorage.getItem(
        "sortByString_" + props.commonDealerId.split("|").join("%7C")
      );
      handleSort(storedSortKey, sortDirection, storedSortByString);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (props.searchEnabled) {
        const keys = Object.keys(sessionStorage);
        for (const key in keys) {
          const value = keys[key];
          if (
            value.startsWith("pageNumber_") ||
            value.startsWith("sortKey_") ||
            value.startsWith("sortDirection") ||
            value.startsWith("sortByString") ||
            value === "scrollPos"
          ) {
            sessionStorage.removeItem(value);
          }
        }
      } else {
        if (
          sessionStorage.getItem(
            "pageNumber_" + props.commonDealerId.split("|").join("%7C")
          ) !== props.currentPage
        ) {
          sessionStorage.setItem(
            "pageNumber_" + props.commonDealerId.split("|").join("%7C"),
            props.currentPage
          );
        }

        sessionStorage.setItem(
          "sortKey_" + props.commonDealerId.split("|").join("%7C"),
          sortKey
        );
        sessionStorage.setItem(
          "sortDirection_" + props.commonDealerId.split("|").join("%7C"),
          sortDir
        );
        sessionStorage.setItem(
          "sortByString_" + props.commonDealerId.split("|").join("%7C"),
          sortByString
        );
      }
    };
  }, [props.searchEnabled, props.commonDealerId, props.currentPage, sortKey, sortDir, sortByString]);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handlePrevious = useCallback(() => {
    props.hideOrShow(true);
    setLoading(true);
    CustomerReservationsClient.getCustomerReservations(
      props.commonDealerId.split("|").join("%7C"),
      props.currentPage - 2,
      props.lang,
      sortByString
    )
      .then((result) => {
        updateState(
          result.reservations,
          result.pagination.numberOfPages,
          result.pagination.totalNumberOfResults,
          props.currentPage - 1
        );
      })
      .catch(() => {})
      .finally(() => {
        props.hideOrShow(false);
        setLoading(false);
      });
  }, [props, sortByString]);

  const handleNext = useCallback(() => {
    props.hideOrShow(true);
    setLoading(true);
    CustomerReservationsClient.getCustomerReservations(
      props.commonDealerId.split("|").join("%7C"),
      props.currentPage,
      props.lang,
      sortByString
    )
      .then((result) => {
        updateState(
          result.reservations,
          result.pagination.numberOfPages,
          result.pagination.totalNumberOfResults,
          props.currentPage + 1
        );
      })
      .catch(() => {})
      .finally(() => {
        props.hideOrShow(false);
        setLoading(false);
      });
  }, [props, sortByString]);

  const handleSort = useCallback((newSortKey, newDirection, newSortByString) => {
    props.hideOrShow(true);
    setSortKey(newSortKey);
    setSortDir(newDirection);
    setSortByString(newSortByString);

    CustomerReservationsClient.getCustomerReservations(
      props.commonDealerId.split("|").join("%7C"),
      props.currentPage - 1,
      props.lang,
      newSortByString
    )
      .then((result) => {
        updateState(
          result.reservations,
          result.pagination.numberOfPages,
          result.pagination.totalNumberOfResults,
          props.currentPage
        );
      })
      .catch(() => {})
      .finally(() => {
        props.hideOrShow(false);
        setLoading(false);
      });
  }, [props]);

  const getSortString = useCallback((newSortKey) => {
    setLoading(true);
    let newDirection = "DSC";
    if (newSortKey === sortKey) {
      newDirection = sortDir === "ASC" ? "DSC" : "ASC";
    }

    if (newSortKey === "date") {
      newDirection === "ASC"
        ? handleSort(newSortKey, newDirection, "byDateAsc")
        : handleSort(newSortKey, newDirection, "byDate");
    } else if (newSortKey === "id") {
      newDirection === "ASC"
        ? handleSort(newSortKey, newDirection, "byOrderNumber")
        : handleSort(newSortKey, newDirection, "byOrderNumberDesc");
    }
  }, [sortKey, sortDir, handleSort]);

  const updateState = useCallback((reservations, numPages, numResults, newPage) => {
    reservations.forEach((reservation) => {
      reservation.orderType =
        reservation.orderType === "pre-order"
          ? props.t("CustomerReservations.preOrder")
          : reservation.orderType;
    });

    props.updateTables(
      reservations,
      numPages,
      numResults,
      props.commonDealerId,
      newPage
    );
  }, [props]);

  const determineDealerId = useCallback(() => {
    const filteredDealer = props.user.dealerGroup.filter(
      (dealer) => dealer.commonId === props.commonDealerId
    );
    if (filteredDealer && filteredDealer.length === 1)
      return filteredDealer[0].completeDealerId;
  }, [props.user.dealerGroup, props.commonDealerId]);

  const determineDealerName = useCallback((dealerName) => {
    if (props.user.dealerGroup) {
      const filteredDealer = props.user.dealerGroup.filter(
        (dealer) => dealer.commonId === props.commonDealerId
      );
      if (filteredDealer && filteredDealer.length === 1) {
        return GET_DEALER_NAME(filteredDealer[0], props.user.market);
      }
    } else return dealerName;
  }, [props.user.dealerGroup, props.commonDealerId, props.user.market]);

  const flipIcon = useCallback(() => {
    setIconUp(prev => !prev);
  }, []);

  const resolveInlineStyle = useCallback((status) => {
    switch (status) {
      case "created":
      case "contracted":
      case "confirmed":
      case "ordered":
        return crStyles.confirmedStatus;
      case "cancelled":
        return crStyles.cancelledStatus;
      case "amended":
        return crStyles.amendedStatus;
      default:
        return crStyles.commonStatusStyle;
    }
  }, []);

  const resolvePurchaseStyle = useCallback((status) => {
    const statusCaps = status ? UpperCaseText(status) : "";
    switch (statusCaps) {
      case PURCHASE_REQUEST_STATUS.ACCEPTED:
        return crStyles.confirmedStatus;
      case PURCHASE_REQUEST_STATUS.CANCELLED:
        return crStyles.cancelledStatus;
      default:
        return crStyles.amendedStatus;
    }
  }, []);

  const getSpecs = useCallback((reservation) => {
    // Just return Spec Summary for all scenario
    return (
      reservation.specSummary &&
      reservation.specSummary.filter(Boolean).join(" / ")
    );
  }, []);

  const translateOrderType = useCallback((reservation) => {
    //Purchase Request
    if (reservation.purchaseType || reservation.purchaseRequestDate) {
      return props.t("CustomerReservations.purchaseRequest");
    }
    //Order
    else if (LowerCaseText(reservation.orderType).includes("order")) {
      return props.t("CustomerReservations.order");
    } else return reservation.orderType;
  }, [props.t]);

  const setStateAndOpenDialog = useCallback((newReservationId) => {
    setReservationId(newReservationId);
    setOpen(true);
  }, []);

  const getTableHeader = useCallback((style, divStyle, column) => {
    return props.fromCrc ? (
      <TableCell id={column + props.commonDealerId} style={style}>
        <div className={divStyle}>
          {props.t("CustomerReservations." + column)}
        </div>
      </TableCell>
    ) : (
      <TableCell
        data-testid={column + props.commonDealerId}
        style={style}
        onClick={() =>
          !props.searchEnabled && getSortString(column)
        }
      >
        <div className={divStyle} data-testid="sort">
          {props.t("CustomerReservations." + column)}
          <SortingIcon
            hide={props.searchEnabled}
            currentKey={column}
            sortKey={sortKey}
            sortDirection={sortDir}
          />
        </div>
      </TableCell>
    );
  }, [props.fromCrc, props.commonDealerId, props.t, props.searchEnabled, getSortString, sortKey, sortDir]);

  if (displayError(hasError)) {
    throw new Error(hasError[0]);
  }

  return (
    <div key={props.index} className={styles.tablePadding}>
      <Accordion
        styles={crStyles.expansionPanel}
        defaultExpanded={true}
        style={{ boxShadow: "none" }}
        onChange={flipIcon}
      >
        <AccordionSummary style={crStyles.boldHeaderBar} id="exp">
          <div className={styles.headerBar}>
            <Grid container>
              <Grid item md={5} style={crStyles.boldHeaderItem}>
                <HasPermissionTo
                  perform={["reservationsRegionalId:view"]}
                  permissions={props.user.permissions.rolePermissions}
                  render={() => (
                    <span className={styles.pre}>
                      {determineDealerId() + "  |  "}
                    </span>
                  )}
                />
                {determineDealerName(props.reservations[0].dealerName)}
              </Grid>
              <Grid item md={2} style={crStyles.boldHeaderItem}>
                {props.totalNumberOfResults}{" "}
                {props.totalNumberOfResults === 1
                  ? props.t("CustomerReservations.item")
                  : props.t("CustomerReservations.items")}
              </Grid>
              <Grid item md={3} />
              <Grid item md={2} style={crStyles.dropdown}>
                {iconUp ? (
                  <ExpandLessIcon id="less" />
                ) : (
                  <ExpandMoreIcon id="more" />
                )}
              </Grid>
            </Grid>
          </div>
          <br />
        </AccordionSummary>
        <div className={styles.bottomBorder}></div>
        <Table id="newTable" key={props.index}>
          {open && (
            <DeliveryScheduleDialog
              {...props}
              open={open}
              onClose={onClose}
              confirmAction={() => {
                props
                  .updateOrder(
                    reservationId,
                    "INSTORE",
                    FORMAT_COMMON_ID(props.commonDealerId)
                  )
                  .then(() => {
                    onClose();
                  })
                  .catch(() => {
                    setHasError([ERROR_PROCESSING]);
                  });
              }}
            />
          )}
          <TableHead>
            <TableRow
              style={crStyles.headers}
              className={styles.orderTableHeaderRow}
            >
              <TableCell
                data-testid={"orderType" + props.commonDealerId}
                style={crStyles.orderTypeHeader}
              >
                <div className={styles.alignStart}>
                  {props.t("CustomerReservations.orderType")}
                </div>
              </TableCell>
              {getTableHeader(
                crStyles.idHeader,
                styles.alignStart,
                "id"
              )}
              {getTableHeader(
                crStyles.dateHeader,
                styles.alignCenter,
                "date"
              )}
              <TableCell
                data-testid={"specs" + props.commonDealerId}
                style={crStyles.specsHeader}
              >
                <div className={styles.alignStart}>
                  {props.t("CustomerReservations.specs")}
                </div>
              </TableCell>
              <TableCell
                data-testid={"status" + props.commonDealerId}
                style={crStyles.statusHeader}
              >
                <div className={styles.alignCenter}>
                  {props.t("CustomerReservations.status")}
                </div>
              </TableCell>
              <TableCell style={crStyles.deliveryScheduledHeader} />
              <TableCell style={crStyles.emptyHeader} />
            </TableRow>
          </TableHead>
          <TableBody
            data-testid="reservationTable"
            className={styles.tableBody}
          >
            {props.reservations.map((reservation, index) => (
              <TableRow
                className="pagePaddingLeft pagePaddingRight"
                id={"row" + index}
                key={index}
              >
                <TableCell style={crStyles.orderType}>
                  {translateOrderType(reservation)}
                </TableCell>
                <TableCell style={crStyles.orderId}>
                  {reservation.id}
                </TableCell>
                <TableCell style={crStyles.date}>
                  {UiDateUtils.formattedDate(reservation.reservationDate)}
                </TableCell>
                <TableCell style={crStyles.specs}>
                  <div className={styles.specsContainer}>
                    <div className={styles.nameplate}>
                      {reservation.nameplate}
                    </div>
                    <span className={styles.specs}>
                      {getSpecs(reservation)}
                    </span>
                  </div>
                </TableCell>
                <TableCell
                  style={
                    reservation.purchaseRequestStatus
                      ? resolvePurchaseStyle(reservation.purchaseRequestStatus)
                      : resolveInlineStyle(STATUS_MAP[reservation.status])
                  }
                >
                  {reservation.purchaseRequestStatus
                    ? TitleCase(reservation.purchaseRequestStatus)
                    : TitleCase(reservation.businessStatus)}
                </TableCell>
                <TableCell style={crStyles.convert}>
                  <HasPermissionTo
                    perform={["processOfflineSubmit"]}
                    permissions={props.user.permissions.rolePermissions}
                    condition={
                      reservation.status === "CREATED" ||
                      reservation.status === "AMENDED"
                    }
                    render={() => (
                      <div
                        className={styles.convertDiv}
                        onClick={() => setStateAndOpenDialog(reservation.id)}
                      >
                        <img alt="CONVERT" src={ConvertButton} />
                        <span className={styles.convertLink}>
                          {props.t("CustomerReservations.deliveryScheduled")}
                        </span>
                      </div>
                    )}
                  />
                </TableCell>

                <TableCell style={crStyles.view}>
                  <HasPermissionTo
                    perform={["crcOrderDetails:view"]}
                    permissions={props.user.permissions.rolePermissions}
                    condition={!props.fromNscDealerSearch}
                    render={() => (
                      <Button
                        className={styles.tableLink}
                        data-testid="crcView"
                        style={crStyles.viewButton}
                        id={reservation.id}
                        onClick={() =>
                          props.showOrderDetails(
                            props.commonDealerId,
                            reservation.id,
                            true
                          )
                        }
                      >
                        {props.t("CustomerReservations.view")}
                        <ChevronRightIcon style={crStyles.viewIcon} />{" "}
                      </Button>
                    )}
                    noRender={() => (
                      <Link
                        id="reservationLink"
                        to={`/dealer/${props.commonDealerId}/customer-reservations/order-details/${reservation.id}`}
                        className={styles.tableLink}
                      >
                        {props.t("CustomerReservations.view")}
                        <ChevronRightIcon style={crStyles.viewIcon} />
                      </Link>
                    )}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <HasPermissionTo
          perform={["reservationPagination:view"]}
          permissions={props.user.permissions.rolePermissions}
          condition={!props.searchEnabled}
          render={() => (
            <div className={styles.pagesDiv}>
              <button
                disabled={props.currentPage === 1 || loading}
                data-testid="prev"
                onClick={handlePrevious}
              >
                <NavigateBefore
                  style={
                    props.currentPage === 1
                      ? crStyles.arrowDisabled
                      : crStyles.arrowEnabled
                  }
                />
              </button>
              <div className={styles.pages}>
                {props.currentPage} / {props.numberOfPages}
              </div>
              <button
                disabled={
                  props.currentPage === props.numberOfPages || loading
                }
                data-testid="next"
                onClick={handleNext}
              >
                <NavigateNext
                  style={
                    props.currentPage === props.numberOfPages
                      ? crStyles.arrowDisabled
                      : crStyles.arrowEnabled
                  }
                />
              </button>
            </div>
          )}
        />
      </Accordion>
    </div>
  );
}

export default withTranslation("emp")(CustomerReservationTable);
